// PraintlDailySale.js

import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  PDFViewer,
  pdf,
} from "@react-pdf/renderer";

import QRCode from "qrcode";

import logo from "./zulu.png";

// ==============================
// STYLES
// ==============================
const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 60,
    paddingHorizontal: 30,
    fontSize: 10,
    fontFamily: "Helvetica",
  },

  // ================= HEADER =================
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    borderBottom: "2 solid #000",
    paddingBottom: 10,
  },

  leftHeader: {
    flexDirection: "row",
    width: "75%",
  },

  logo: {
    width: 70,
    height: 70,
    marginRight: 15,
  },

  titleContainer: {
    flexDirection: "column",
  },

  title: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 5,
  },

  subTitle: {
    fontSize: 10,
    marginBottom: 2,
  },

  // ================= REPORT INFO =================
  reportInfo: {
    marginBottom: 15,
    alignItems: "flex-start",
  },

  infoText: {
    fontSize: 10,
    marginBottom: 3,
  },

  // ================= TABLE =================
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
  },

  tableRow: {
    flexDirection: "row",
  },

  tableHeader: {
    backgroundColor: "#111827",
    color: "#fff",
  },

  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    padding: 5,
  },

  col1: {
    width: "8%",
  },

  col2: {
    width: "26%",
  },

  col3: {
    width: "12%",
  },

  col4: {
    width: "20%",
  },

  col5: {
    width: "14%",
  },

  col6: {
    width: "20%",
  },

  tableCell: {
    fontSize: 9,
  },

  tableHeaderText: {
    fontSize: 10,
    fontWeight: "bold",
  },

  // ================= FOOTER =================
  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },

  pageNumber: {
    fontSize: 10,
  },

  qrContainer: {
    alignItems: "flex-end",
  },

  qrImage: {
    width: 70,
    height: 70,
  },

  totalSection: {
    marginTop: 20,
    alignItems: "flex-end",
  },

  totalText: {
    fontSize: 12,
    fontWeight: "bold",
  },
});

// ==============================
// PDF DOCUMENT
// ==============================
const DailySalesPDFDocument = ({
  sales = [],
  selectedDate,
  purchasedBy,
}) => {
  // ================= TOTAL =================
  const grandTotal = sales.reduce(
    (sum, item) => sum + Number(item.total_price || 0),
    0
  );

  // ================= QR DATA =================
  const qrData = JSON.stringify({
    date: selectedDate,
    purchasedBy,
    totalSales: grandTotal,
    records: sales,
  });

  const [qrCode, setQrCode] = React.useState("");

  React.useEffect(() => {
    QRCode.toDataURL(qrData).then(setQrCode);
  }, [qrData]);

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>

        {/* ================= FIRST PAGE HEADER ================= */}
        <View fixed>
          <View style={styles.headerContainer}>

            <View style={styles.leftHeader}>

              <Image src={logo} style={styles.logo} />

              <View style={styles.titleContainer}>
                <Text style={styles.title}>
                  Zuluwhiskey Daily Financial Report
                </Text>

                <Text style={styles.subTitle}>
                  Johnsonville, Monrovia Liberia
                </Text>

                <Text style={styles.subTitle}>
                  Email: info@zuluwhiskeyshipping.com
                </Text>

                <Text style={styles.subTitle}>
                  Contact: +231-77-327-5388 / +231-889-239-865
                </Text>
              </View>

            </View>

          </View>

          {/* ================= REPORT INFO ================= */}
          <View style={styles.reportInfo}>
            <Text style={styles.infoText}>
              {/* Purchased By: {purchasedBy || "All Customers"} */}
              Sold Out Stock For The Date Below:
            </Text>

            <Text style={styles.infoText}>
              Report Date: {selectedDate}
            </Text>
          </View>
        </View>

        {/* ================= TABLE ================= */}
        <View style={styles.table}>

          {/* HEADER */}
          <View style={[styles.tableRow, styles.tableHeader]}>

            <View style={[styles.tableCol, styles.col1]}>
              <Text style={styles.tableHeaderText}>#</Text>
            </View>

            <View style={[styles.tableCol, styles.col2]}>
              <Text style={styles.tableHeaderText}>Product</Text>
            </View>

            <View style={[styles.tableCol, styles.col3]}>
              <Text style={styles.tableHeaderText}>Qty</Text>
            </View>

            <View style={[styles.tableCol, styles.col4]}>
              <Text style={styles.tableHeaderText}>Sold By</Text>
            </View>

            <View style={[styles.tableCol, styles.col5]}>
              <Text style={styles.tableHeaderText}>Unit</Text>
            </View>

            <View style={[styles.tableCol, styles.col6]}>
              <Text style={styles.tableHeaderText}>Total</Text>
            </View>

          </View>

          {/* BODY */}
          {sales.map((item, index) => (
            <View style={styles.tableRow} key={index} wrap={false}>

              <View style={[styles.tableCol, styles.col1]}>
                <Text style={styles.tableCell}>
                  {index + 1}
                </Text>
              </View>

              <View style={[styles.tableCol, styles.col2]}>
                <Text style={styles.tableCell}>
                  {item.product_name}
                </Text>
              </View>

              <View style={[styles.tableCol, styles.col3]}>
                <Text style={styles.tableCell}>
                  {item.quantity_taken}
                </Text>
              </View>

              <View style={[styles.tableCol, styles.col4]}>
                <Text style={styles.tableCell}>
                  {item.purchased_by_name}
                </Text>
              </View>

              <View style={[styles.tableCol, styles.col5]}>
                <Text style={styles.tableCell}>
                  ${Number(item.unit_price).toFixed(2)}
                </Text>
              </View>

              <View style={[styles.tableCol, styles.col6]}>
                <Text style={styles.tableCell}>
                  ${Number(item.total_price).toFixed(2)}
                </Text>
              </View>

            </View>
          ))}

        </View>

        {/* ================= TOTAL ================= */}
        <View style={styles.totalSection}>
          <Text style={styles.totalText}>
            Grand Total: ${grandTotal.toFixed(2)}
          </Text>
        </View>

        {/* ================= FOOTER ================= */}
        <View style={styles.footer} fixed>

          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />

          {qrCode && (
            <View style={styles.qrContainer}>
              <Image src={qrCode} style={styles.qrImage} />
            </View>
          )}

        </View>

      </Page>
    </Document>
  );
};

export default DailySalesPDFDocument;